import React, { forwardRef, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { mergeRefs } from '@utils/mergeRefs';
import styles from './Modal.module.css';

export type ModalLayer = 'overlay-content' | 'modal' | 'session-warning';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * `popover`: a transparent overlay that closes on click, around a panel that slides in.
 * `dialog`: a centred panel styled only by `className`, closed only by Escape or the caller.
 */
export type ModalVariant = 'popover' | 'dialog';

const LAYER_CLASS: Record<ModalLayer, string | undefined> = {
  'overlay-content': styles.overlayContentLayer,
  modal: undefined,
  'session-warning': styles.sessionWarningLayer,
};

/** ...rest and the ref go to the role="dialog" node; className goes to the content panel. */
export interface IModalProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onClick' | 'role' | 'aria-modal'
> {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  position?: { top: number; right: number };
  variant?: ModalVariant;
  layer?: ModalLayer;
  backdropClassName?: string;
  /** false renders in place, so the layers share the caller's stacking context. */
  portal?: boolean;
  /** Focused on open; whatever had focus before gets it back on close. */
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  /** While true the panel plays its exit animation; `onExited` fires when that animation ends. */
  isClosing?: boolean;
  onExited?: () => void;
}

export const Modal = forwardRef<HTMLDivElement, IModalProps>(
  (
    {
      isOpen,
      onClose,
      children,
      className,
      position,
      variant = 'popover',
      layer = 'modal',
      backdropClassName,
      portal = true,
      initialFocusRef,
      isClosing = false,
      onExited,
      ...rest
    },
    ref,
  ) => {
    const panelRef = useRef<HTMLDivElement>(null);
    const dialogRef = useRef<HTMLDivElement>(null);
    const onExitedRef = useRef(onExited);
    onExitedRef.current = onExited;

    useEffect(() => {
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape' && isOpen) {
          event.preventDefault();
          onClose();
        }
      };

      if (isOpen) {
        document.addEventListener('keydown', handleEscape);
        // Prevent body scroll
        document.body.style.overflow = 'hidden';
      }

      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = 'unset';
      };
    }, [isOpen, onClose]);

    // aria-modal promises assistive tech that the page behind is out of reach, so Tab must not get there.
    useEffect(() => {
      if (!isOpen || variant !== 'dialog') return;
      const handleTab = (event: KeyboardEvent) => {
        const node = dialogRef.current;
        if (event.key !== 'Tab' || !node) return;
        const focusable = [...node.querySelectorAll<HTMLElement>(FOCUSABLE)];
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        const outside = !node.contains(active);
        if (event.shiftKey && (active === first || active === node || outside)) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (active === last || outside)) {
          event.preventDefault();
          first.focus();
        }
      };
      document.addEventListener('keydown', handleTab);
      return () => document.removeEventListener('keydown', handleTab);
    }, [isOpen, variant]);

    useEffect(() => {
      if (!isOpen || !initialFocusRef) return;
      const previouslyFocused = document.activeElement as HTMLElement | null;
      initialFocusRef.current?.focus();
      return () => previouslyFocused?.focus?.();
    }, [isOpen, initialFocusRef]);

    useEffect(() => {
      const panel = panelRef.current;
      if (!isClosing || !panel) return;
      // With no animation running, no animationend will ever arrive.
      if (panel.getAnimations?.().length === 0) {
        onExitedRef.current?.();
        return;
      }
      const handleEnd = (event: AnimationEvent) => {
        if (event.target === panel) onExitedRef.current?.();
      };
      panel.addEventListener('animationend', handleEnd);
      return () => panel.removeEventListener('animationend', handleEnd);
    }, [isClosing]);

    if (!isOpen) return null;

    const dialog = variant === 'dialog';

    const tree = (
      <div
        className={[styles.modalBackdrop, LAYER_CLASS[layer], backdropClassName]
          .filter(Boolean)
          .join(' ')}
      >
        <div
          {...rest}
          ref={mergeRefs(dialogRef, ref)}
          className={
            dialog ? `${styles.modalOverlay} ${styles.dialogOverlay}` : styles.modalOverlay
          }
          onClick={dialog ? undefined : onClose}
          role="dialog"
          aria-modal="true"
        >
          <div
            ref={panelRef}
            className={dialog ? className : `${styles.modalContent} ${className || ''}`}
            onClick={(e) => e.stopPropagation()}
            style={
              position
                ? {
                    position: 'fixed',
                    top: `${position.top}px`,
                    right: `${position.right}px`,
                    transform: 'none',
                  }
                : {}
            }
          >
            {children}
          </div>
        </div>
      </div>
    );

    return portal ? createPortal(tree, document.body) : tree;
  },
);

Modal.displayName = 'Modal';
